<script lang="ts" setup>
import { computed, inject, onMounted, PropType, Ref, ref } from 'vue';
import {
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
  TirPmScrollbar,
  TirPmDraggableSorting,
  DragModel,
} from 'tir-components';
import { StarIcon } from 'tir-style-system/icons/solid';
import { LabelContent } from '@/components';
import { OrderTaskModel, TaskColumnModel } from '../../models';
import { TaskHeader } from '../../components';
import TaskKanbanCardViewSettingsDropdown from '@/components/tasks-kanban/components/TaskKanbanCardViewSettingsDropdown.vue';
import { TaskViewElementEnum } from '@/components/tasks-kanban/enums';
import TaskOrderLi from './task-order-li.vue';

// #region defineProps
const props = defineProps({
  /**id юзера */
  userId: { type: Number },
  /**id юзера из роутера */
  routerUserId: { type: Number },
  /**Список задач */
  tasks: {
    type: Array as PropType<Array<OrderTaskModel>>,
    required: true,
  },
  /**Открывать при монтировании компонента */
  autoOpen: {
    type: Boolean,
    default: false,
  },
  /**Загрузка */
  isLoading: {
    type: Boolean,
    default: false,
  },
  /**Доступ на редактирование всего списка очередности */
  fullChangeAccessOrder: {
    type: Boolean,
    default: false,
  },
});
// #endregion defineProps

// #region defineEmits
const emit = defineEmits<{
  /**Событие обновления списка задч */
  change: [{ Tasks: Array<OrderTaskModel>; MovedTaskId?: number }];
  /**Событие открытия шторки задачи */
  openTaskDrawer: [taskId: number];
  /**Событие удаления задачи из очередности */
  deleteTaskOrder: [taskId: number];
  /**Событие изменения порядка задач */
  changeTasks: [taskId: number];
}>();
// #endregion defineEmits

// #region Переменные
/**Колонки */
const columns: TaskColumnModel[] = [
  new TaskColumnModel({
    Type: TaskViewElementEnum.Project,
    Name: '',
    Width: '1.5rem',
    MinWidth: '1.5rem',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Namespace,
    Name: '',
    Width: '1.5rem',
    MinWidth: '1.5rem',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Queue,
    Name: 'Очередь',
    Width: '9.375rem',
    MinWidth: '9.375rem',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Number,
    Name: '№',
    Width: '5rem',
    MinWidth: '5rem',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Name,
    Name: 'Название',
    Width: '50vw',
    MinWidth: '20rem',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Status,
    Name: 'Статус',
    Width: '5.1875rem',
    MinWidth: '5.1875rem',
    Align: 'center',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Priority,
    Name: 'Приоритет',
    Width: '4rem',
    MinWidth: '4rem',
    Align: 'center',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Difficulty,
    Name: 'Сложность',
    Width: '2rem',
    MinWidth: '2rem',
    Align: 'center',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.EstimateTime,
    Name: 't (оценка)',
    Width: '5rem',
    MinWidth: '5rem',
    Align: 'center',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.WorkTime,
    Name: 't (затрачено)',
    Width: '5rem',
    MinWidth: '5rem',
    Align: 'center',
  }),
];
/**Шаг добавления задач по кнопке "Показать еще" */
const STEP_SHOW_TASKS = 10;
// #endregion Переменные

// #region refs
/**Кол-во видимых задач */
const countVisibleTasks = ref(STEP_SHOW_TASKS);
/**Список открыт */
const isOpened = ref(props.autoOpen);
/**Элемент обертки */
const $wrapperElement = inject<Ref<HTMLElement | undefined>>('wrapperElement', ref(undefined));
const viewSettings = ref([
  TaskViewElementEnum.Project,
  TaskViewElementEnum.Namespace,
  TaskViewElementEnum.Queue,
  TaskViewElementEnum.Name,
  TaskViewElementEnum.Number,
  TaskViewElementEnum.Status,
  TaskViewElementEnum.Priority,
  TaskViewElementEnum.Difficulty,
  TaskViewElementEnum.EstimateTime,
  TaskViewElementEnum.WorkTime,
]);
/**Перемещаемый элемент */
const draggbleElement = ref<DragModel<OrderTaskModel>>();
// #endregion refs

// #region computed
/**Текст заголовка */
const headerText = computed(() => {
  return `Очередность для выполнения (${props.tasks.length})`;
});
/**Задачи которые запрещено перемещать */
const tasksChangeNoAccess = computed(() => props.tasks.filter((task) => !task.ChangeAccess));
/**Задачи которые разрешено перемещать */
const tasksChangeAccess = computed(() => props.tasks.filter((task) => task.ChangeAccess));
/**Видимые задачи с запретом на перемещение */
const visibleTasksChangeNoAccess = computed(() =>
  tasksChangeNoAccess.value.slice(0, countVisibleTasks.value),
);
/**Видимые задачи с разрешением на перемещение */
const visibleTasksChangeAccess = computed(() =>
  tasksChangeAccess.value.slice(
    0,
    Math.max(0, countVisibleTasks.value - visibleTasksChangeNoAccess.value.length),
  ),
);

/**Показывать кнопку "Показать еще" */
const isVisibleShowButton = computed(() => props.tasks.length > countVisibleTasks.value);

/**Задачи для перетаскивания */
const dragTasks = computed(() => {
  return (
    visibleTasksChangeAccess.value?.map(
      (task) =>
        new DragModel({
          Data: task,
          AllowDrag: tasksChangeAccess.value.length > 1,
          AllowDrop: false,
        }),
    ) || []
  );
});
// #endregion computed

// #region хуки жизненного цикла
onMounted(() => {
  if (props.routerUserId === props.userId) {
    updateIsOpened(false);
  }
});
// #region Функции

const onDrag = (isDrag: boolean, dragItem: DragModel<OrderTaskModel>) => {
  if (isDrag) {
    draggbleElement.value = dragItem;
  }
};
/**
 * Удалить задачу из списка очередности
 * @param taskId ID Задачи
 */
const removeTask = (taskId: number) => {
  // emit("change", {
  //   Tasks: props.tasks.filter((task) => task.Id !== taskId),
  // });
  emit('deleteTaskOrder', taskId);
};
/**При обновлении флага isOpened */
const updateIsOpened = (value: boolean) => {
  isOpened.value = value;
  if (!isOpened) {
    countVisibleTasks.value = STEP_SHOW_TASKS;
  }
};

/**Показать еще задачи */
const showMore = () => {
  countVisibleTasks.value += STEP_SHOW_TASKS;
};

const onChangeOrder = (data: Array<DragModel<OrderTaskModel>>) => {
  const newTasks = [
    ...visibleTasksChangeNoAccess.value,
    ...data.map((item) => item.Data),
    ...props.tasks.slice(countVisibleTasks.value),
  ];
  emit('change', { Tasks: newTasks, MovedTaskId: draggbleElement.value?.Data?.Id });
  emit('changeTasks', draggbleElement.value?.Data?.Id);
};
// #endregion Функции
</script>

<template>
  <div class="tasks-order-list__wrapper">
    <LabelContent
      class="tasks-order-list"
      :delay="0"
      flag-mode
      :visible="isOpened"
      @update:visible="updateIsOpened"
    >
      <template #header>
        <div class="tasks-order-list__header">
          <StarIcon class="tasks-order-list__header-icon" />
          {{ headerText }}
        </div>
      </template>
      <template #default>
        <template v-if="tasks?.length">
          <div class="tasks-order-list__settings-header">
            <TaskKanbanCardViewSettingsDropdown tasks-order-pages v-model="viewSettings" />
          </div>
          <TirPmScrollbar>
            <div>
              <div class="tasks-order-list__main">
                <TaskHeader
                  class="tasks-order-list__columns-header"
                  :columns="columns"
                  :enable-sorting="false"
                  :view-settings="viewSettings"
                />
                <div class="tasks-order-list__tasks">
                  <div
                    class="tasks-order-list__tasks-no-access-list"
                    v-if="visibleTasksChangeNoAccess?.length"
                  >
                    <TaskOrderLi
                      v-for="task in visibleTasksChangeNoAccess"
                      :task="task"
                      :columns="columns"
                      :view-settings="viewSettings"
                      :is-loading-delete="isLoading"
                      :key="task.Id"
                      @open-task-drawer="emit('openTaskDrawer', $event)"
                    />
                  </div>
                  <TirPmDraggableSorting
                    data-class="tasks-order-list__drag-task"
                    :data="dragTasks"
                    :is-allow-scroll="true"
                    :scroll-area="$wrapperElement"
                    :disable-side-indent="true"
                    :is-allow-click-delay="true"
                    :indent="8"
                    @change="onChangeOrder"
                    @drag-component-on-drag="onDrag"
                  >
                    <template #item="props">
                      <div>
                        <TaskOrderLi
                          :task="props.item"
                          :columns="columns"
                          :view-settings="viewSettings"
                          :is-loading-delete="isLoading"
                          :full-change-access-order="fullChangeAccessOrder"
                          @remove="removeTask"
                          @open-task-drawer="emit('openTaskDrawer', $event)"
                        />
                      </div>
                    </template>
                  </TirPmDraggableSorting>
                </div>
              </div>
            </div>
          </TirPmScrollbar>
          <TirPmButton
            v-if="isVisibleShowButton"
            :variant="TirPmButtonVariantEnum.SecondaryState"
            :size="TirPmButtonSizeEnum.Small"
            style="width: 100%"
            @click="showMore"
          >
            Показать ещё
          </TirPmButton>
        </template>
        <div v-else-if="tasks" class="tasks-order-list__empty-placeholder">Задачи отсутствуют</div>
      </template>
    </LabelContent>
  </div>
</template>

<style lang="scss" scoped>
.tasks-order-list {
  &__header {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    color: var(--text-primary);

    &-icon {
      width: 1.5rem;
      height: 1.5rem;
    }
  }

  &__main {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-left: 2.5rem;
    margin-bottom: 0.5rem;
  }

  &__tasks {
    display: flex;
    flex-direction: column;
  }

  &__empty-placeholder {
    color: var(--neutral-50);
    margin-left: 2.5rem;
  }

  &__settings-header {
    margin-bottom: 1.125rem;
  }
  &__tasks-no-access-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
}
</style>
<style lang="scss">
.tasks-order-list {
  .button__sign-main {
    display: flex;
  }

  & > .label-content_body {
    margin-top: 0.625rem;

    &:empty {
      display: none;
    }
  }
}

.tasks-order-list__drag-task.dragging {
  opacity: 0.5;
  cursor: move;

  * {
    cursor: move;
  }

  .task-order-li__remove-button {
    display: none;
  }
}
</style>
